import { useState, useEffect } from 'react';
import { PRODUCT_PAGES, PAID_ADS_LANDING_PAGES } from '../constants';

/**
 * Hook for managing interactive demo popup with exit intent and time-based triggers
 */
const useInteractiveDemoPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasShown, setHasShown] = useState(false);

  // Check if we're on desktop (not mobile)
  const isDesktop = () => {
    return window.innerWidth >= 1024; // lg breakpoint
  };

  // Check if user has already seen the popup (session-based)
  const hasSeenPopup = () => {
    const hasSeenBefore = sessionStorage.getItem('demo-popup-seen');
    return hasSeenBefore === 'true';
  };

  const isProductPage = () => {
    if (typeof window === 'undefined') return false;
    const currentPath = window.location?.pathname || '';
    return typeof currentPath === 'string' && Array.isArray(PRODUCT_PAGES) && PRODUCT_PAGES.some(page => currentPath.includes(page));
  };

  const isPaidAdsLandingPage = () => {
    if (typeof window === 'undefined') return false;
    const currentPath = window.location?.pathname || '';
    return typeof currentPath === 'string' && Array.isArray(PAID_ADS_LANDING_PAGES) && PAID_ADS_LANDING_PAGES.some(page => currentPath.includes(page));
  };

  const isPricingPage = () => {
    if (typeof window === 'undefined') return false;
    const currentPath = window.location?.pathname || '';
    return currentPath === '/pricing';
  };

  const isBlogPage = () => {
    if (typeof window === 'undefined') return false;
    const currentPath = window.location?.pathname || '';
    return typeof currentPath === 'string' && currentPath.includes('/blog/');
  };

  const isOrganicEntry = () => {
    if (typeof window === 'undefined') return false;
    const referrer = document?.referrer || '';
    // Check if coming from search engines (organic)
    const organicSources = ['google.', 'bing.', 'yahoo.', 'duckduckgo.', 'search.'];
    return typeof referrer === 'string' && organicSources.some(source => referrer.includes(source));
  };

  const openPopup = () => {
    if (!hasShown && isDesktop() && !hasSeenPopup()) {
      setIsOpen(true);
      setHasShown(true);
      
      // Mark as seen for this session
      sessionStorage.setItem('demo-popup-seen', 'true');
      
      // Prevent body scroll
      document.body.style.overflow = "hidden";
    }
  };

  const forceOpenPopup = () => {
    setIsOpen(true);
    setHasShown(true);
    // Prevent body scroll
    document.body.style.overflow = "hidden";
  };

  const closePopup = () => {
    setIsOpen(false);
    // Restore body scroll
    document.body.style.overflow = "";
  };

  // Define handleBeforeUnload once so it's available for add/removeEventListener
  const handleBeforeUnload = (e) => {
    // This won't show our popup but we could track the intent
    if (!hasShown) {
      // Could potentially show popup here, but browsers limit this
    }
  };

  useEffect(() => {
    // Early bailout checks to minimize performance impact
    if (!isDesktop()) {
      return;
    }
    if (hasShown) {
      return;
    }
    if (hasSeenPopup()) {
      return;
    }
    if (isPricingPage()) {
      return;
    }

    const currentIsProductPage = isProductPage();
    const currentIsPaidAdsLandingPage = isPaidAdsLandingPage();
    const currentIsBlogPage = isBlogPage();
    const currentIsOrganic = isOrganicEntry();

    // Determine if popup should be enabled on this page
    const shouldShowPopup = currentIsProductPage || currentIsPaidAdsLandingPage || currentIsBlogPage;
    
    if (!shouldShowPopup) {
      return; // No popup on this page - exit early without adding listeners
    }

    let timeBasedTimer = null;

    // Set up time-based trigger ONLY for product pages (25 seconds)
    if (currentIsProductPage) {
      timeBasedTimer = setTimeout(() => {
        openPopup();
      }, 25000); // 25 seconds before showing
    }

    // Set up exit intent detection for product pages, paid ads landing pages, and organic blog entries
    if (currentIsProductPage || currentIsPaidAdsLandingPage || currentIsBlogPage) {
      const handleMouseLeave = (e) => {
        // Only trigger if mouse leaves from the top of the viewport
        if (e.clientY <= 0 && !hasShown) {
          openPopup();
        }
      };
      document.addEventListener('mouseleave', handleMouseLeave);
      window.addEventListener('beforeunload', handleBeforeUnload);
      // Cleanup function
      return () => {
        if (timeBasedTimer) {
          clearTimeout(timeBasedTimer);
        }
        document.removeEventListener('mouseleave', handleMouseLeave);
        window.removeEventListener('beforeunload', handleBeforeUnload);
        // Always restore scroll on unmount
        document.body.style.overflow = "";
      };
    }
    // Cleanup for time-based only
    return () => {
      if (timeBasedTimer) {
        clearTimeout(timeBasedTimer);
      }
      document.body.style.overflow = "";
    };
  }, [hasShown]);

  // Handle window resize to check if we're still on desktop
  useEffect(() => {
    const handleResize = () => {
      if (!isDesktop() && isOpen) {
        closePopup(); // Close popup if switching to mobile
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      // Always restore scroll on unmount
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Add global console trigger for testing
  useEffect(() => {
    window.triggerDemoPopup = forceOpenPopup;
    window.resetDemoPopup = () => {
      sessionStorage.removeItem('demo-popup-seen');
      setHasShown(false);
      setIsOpen(false);
    };
    
    return () => {
      delete window.triggerDemoPopup;
      delete window.resetDemoPopup;
      // Always restore scroll on unmount
      document.body.style.overflow = "";
    };
  }, []);

  return {
    isOpen,
    openPopup: () => openPopup(), // Manual trigger if needed
    closePopup,
    hasShown
  };
};

export default useInteractiveDemoPopup; 